import { inject } from '@angular/core';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { SessionService } from '@monsido/core/session/session.service';
import { CoreDomainService } from '@client/app/core/services/domain/core-domain.service';
import { ApiService } from '@client/app/services/api/api-service/api.service';
import { TotalDomainsService } from '@client/app/services/total-domains/total-domains.service';
import { LabelTableService } from '@client/app/services/label-table/label-table.service';
import { MonDialog } from '@client/app/modules/dialog-upgrade-wrapper/mon-dialog.service';
import { ExtensionService } from '@client/app/modules/extension/services/extension.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { OauthService } from '@monsido/oauth/oauth.service';
import { proxyApiPath } from '@monsido/ng2/core/constants/general.constant';
import { RollbarErrorHandlerService } from '@client/ng2/external/rollbar/rollbar-error-handler.service';
import { VirtualUrlService } from '@client/app/modules/business-intelligence/services/virtual-urls.service';
import { BiService } from '@client/app/modules/business-intelligence/services/bi.service';
import { ErrorHandler } from '@client/app/core/services/error-handler/error-handler';
import { UserService } from '@client/app/services/entities/user/user/user.service';
import { CacheService } from '@client/app/modules/cache/services/cache.service';
import { BehaviorService } from '@client/app/modules/behavior/service/behavior.service';

export function globalEventListeners () {
    const sessionService = inject(SessionService);
    const monEventsService = inject(MonEventService);
    const coreDomainService = inject(CoreDomainService);
    const apiService = inject(ApiService);
    const oauthService = inject(OauthService);
    const labelTableService = inject(LabelTableService);
    const monDialog = inject(MonDialog);
    const extensionService = inject(ExtensionService);
    const totalDomainsService = inject(TotalDomainsService);
    const rollbarErrorHandlerService = inject(RollbarErrorHandlerService);
    const virtualUrlService = inject(VirtualUrlService);
    const biService = inject(BiService);
    const errorHandler = inject(ErrorHandler);
    const userService = inject(UserService);
    const cacheService = inject(CacheService);

    monDialog.init((params) => {
        try {
            const virtualUrl = virtualUrlService.get(params.body, params);
            const newParams = virtualUrlService.getParams(params.body, params);
            const ref = virtualUrlService.getRef(params);
            const title = params.title || 'Undefined';
            biService.pageView(virtualUrl, title, ref, true, newParams);
        } catch (e: unknown) {
            errorHandler.noopError(e as string, 'error');
        }
    });

    monEventsService.addListener('afterCustomer', async () => {
        if (sessionService.agreement?.admin) {
            cacheService.setConfig({ maxAge: 3600000 });
            userService.tryGetUsersFromCache({ page_size: 50, page: 1 }).then();
        }
        coreDomainService.init();
        await coreDomainService.getDomains();
    });

    monEventsService.addListener('logout', function () {
        coreDomainService.disposeDomains();
        coreDomainService.disposeDomain();

        apiService.removeHeader('Authorization');
        apiService.removeHeader('Monsido-Account-Id');

        rollbarErrorHandlerService.setConfigPayload({
            person: null,
            customer: null,
        });

        oauthService.clear();
    });

    monEventsService.addListener('clearAgreement', function () {
        totalDomainsService.resetDomains();
        coreDomainService.cancelDomainPromise();
        sessionService.roles = [];
    });

    monEventsService.addListener('beforeLoadUser', function () {
        apiService.setHeader('Authorization', oauthService.getTokenHeaderValue());
        apiService.setUrlPrefix(proxyApiPath + '/api/');
    });

    monEventsService.addListener('newAccessToken', () => {
        apiService.setHeader('Authorization', oauthService.getTokenHeaderValue());
    });

    monEventsService.addListener('setAgreement', function (agreement) {
        apiService.setHeader('Monsido-Account-Id', agreement.account.id);

        labelTableService.setEditable(sessionService.hasRole('customer_admin')); // Set allow Edit in Labels
        labelTableService.canAssignLabels = true;
        labelTableService.canManageLabels = sessionService.hasRole('customer_admin');
        sessionService.accessibilityMode = agreement.settings.accessibility;

        rollbarErrorHandlerService.setConfigPayload({
            customer: {
                id: agreement.account.id,
                name: agreement.account.name,
            },
        });
    });

    monEventsService.addListener(MON_EVENTS.OPEN_EXTENSION_DEEPLINK, async function (payload) {
        await extensionService.openExtensionDeeplink(payload);
    });

    monEventsService.addListener('newException', (errorMsg, severity) => {
        switch (severity) {
            case 'debug':
            case 'info':
                rollbarErrorHandlerService.info(errorMsg);
                break;
            case 'warning':
                rollbarErrorHandlerService.warn(errorMsg);
                break;
            case 'error':
                rollbarErrorHandlerService.error(errorMsg);
                break;
            case 'critical':
                rollbarErrorHandlerService.critical(errorMsg);
                break;
            default:
                rollbarErrorHandlerService.error(errorMsg);
        }
    });
}

export function behaviorEventListeners () {
    const monEventService = inject(MonEventService);
    const behaviorService = inject(BehaviorService);

    monEventService.addListener('intercomShow', () => {
        behaviorService.openIntercom();
    });

    monEventService.addListener('domainGroupAdded', params => {
        behaviorService.addDomainGroup(params.domain.url, params.group.title);
    });

    monEventService.addListener('policyAdded', params => {
        behaviorService.addPolicy(params.name);
    });

    monEventService.addListener('policyUpdated', params => {
        behaviorService.updatePolicy(params.name);
    });

    monEventService.addListener('domainGroupUpdated', params => {
        behaviorService.updateDomainGroup(params.domain.url, params.group.title);
    });

    monEventService.addListener('domainAdded', params => {
        behaviorService.addNewDomain(params.url, params.title);
    });

    monEventService.addListener('userAdded', params => {
        behaviorService.addUser(params.email, params.customer_admin);
    });

    monEventService.addListener('userDeleted', params => {
        behaviorService.deleteUser(params.email);
    });

    monEventService.addListener('openFasttrack', () => {
        behaviorService.openFasttrack();
    });

    monEventService.addListener('fasttrackIgnore', params => {
        behaviorService.fasttrackIgnore(params);
    });

    monEventService.addListener('remediationBtnClick', params => {
        behaviorService.remediationBtnClick(params.url, params.title);
    });

    monEventService.addListener('remediationCreated', params => {
        behaviorService.remediationCreated(params.url, params.title);
    });

    monEventService.addListener('remediationMultiBtnClick', params => {
        behaviorService.remediationMultiBtnClick(params.files);
    });
}
